import {
	getDueDateNavigation,
	isEPRDueDate,
	isSalesTaxDueDate,
	isVatDueDate,
} from '../../DueDates/utils'
import {
	OPEN_RETURN_PREVIEW_ID_KEY,
	OPEN_RETURN_PREVIEW_ITEM_KEY,
} from '../../../utils/openReturnPreviewFromLocation'
import { formatProgressChangedAt } from '../reportCalendarDateUtils'
import {
	formatReportDueDateLabel,
	formatReportFrequency,
	formatReportJurisdiction,
	formatReportPeriod,
	mapReportItemToVariant,
} from '../reportCalendarMappers'

const normalizeProgressKey = (value) => String(value ?? '').trim().toLowerCase()

const hasChangedAtKey = (step) => (
	step != null
	&& typeof step === 'object'
	&& Object.prototype.hasOwnProperty.call(step, 'changed_at')
	&& step.changed_at != null
	&& String(step.changed_at).trim() !== ''
)

export const mapProgressStepsFromApi = (progress) => {
	const steps = Array.isArray(progress?.steps) ? progress.steps : []
	if (steps.length === 0) {
		return []
	}

	const currentKey = normalizeProgressKey(progress.current)
	let currentIndex = steps.findIndex(
		(step) => normalizeProgressKey(step?.key) === currentKey,
	)

	if (currentIndex < 0) {
		currentIndex = steps.reduce(
			(lastSeen, step, index) => (hasChangedAtKey(step) ? index : lastSeen),
			-1,
		)
	}

	const lastIndex = steps.length - 1
	const isLoadingKey = (value) => {
		const key = normalizeProgressKey(value)
		return key === 'loading' || key === 'processing'
	}

	return steps.map((step, index) => {
		const key = step?.key != null && step.key !== '' ? String(step.key) : `step-${index}`
		const hasChangedAt = hasChangedAtKey(step)
		let status = 'pending'

		if (hasChangedAt && currentIndex >= 0) {
			if (index < currentIndex) {
				status = 'completed'
			} else if (index === currentIndex) {
				if (isLoadingKey(currentKey) || isLoadingKey(key)) {
					status = 'loading'
				} else {
					status = index === lastIndex ? 'completed' : 'in_progress'
				}
			}
		}

		return {
			id: key,
			key,
			label: step?.label || key,
			status,
			hasChangedAt,
			changedAt: hasChangedAt ? step.changed_at : null,
			changedAtLabel: formatProgressChangedAt(hasChangedAt ? step.changed_at : null),
		}
	})
}

export const getReportCalendarReportId = (item) => {
	if (item == null || typeof item !== 'object') {
		return null
	}

	const value = item.report_id ?? item.reportId
	if (value == null) {
		return null
	}

	const normalized = String(value).trim()
	if (!normalized || normalized === '0' || normalized.toLowerCase() === 'null') {
		return null
	}

	return value
}

export const hasReportCalendarReportId = (item) => getReportCalendarReportId(item) != null

/** Missing `changed_at` on progress steps means there is no declaration yet. */
export const hasReportCalendarDeclaration = (item) => {
	const steps = item?.progress?.steps
	if (Array.isArray(steps) && steps.length > 0) {
		return steps.some(hasChangedAtKey)
	}

	return hasReportCalendarReportId(item)
}

export const canViewReportCalendarReturn = (item) => (
	hasReportCalendarReportId(item) && hasReportCalendarDeclaration(item)
)

const buildViewReturnPreviewItem = (item = {}) => ({
	name: item.type_of_report || item.name || '',
	period: item.period,
	period_type: item.period_type,
	period_year: item.period_year,
	customer_country: item.country
		? { name: item.country }
		: undefined,
})

const buildViewReturnNavigation = (pathname, reportId, item) => ({
	pathname,
	state: {
		[OPEN_RETURN_PREVIEW_ID_KEY]: reportId,
		[OPEN_RETURN_PREVIEW_ITEM_KEY]: buildViewReturnPreviewItem(item),
	},
})

export const getReportCalendarReturnNavigation = (item = {}, projectName) => {
	const reportId = getReportCalendarReportId(item)

	if (canViewReportCalendarReturn(item) && reportId != null) {
		if (isVatDueDate(item)) {
			return buildViewReturnNavigation('/vat-returns', reportId, item)
		}

		if (isSalesTaxDueDate(item)) {
			return buildViewReturnNavigation('/tax-returns', reportId, item)
		}

		if (isEPRDueDate(item)) {
			return buildViewReturnNavigation('/epr-reporting', reportId, item)
		}
	}

	return getDueDateNavigation(item, projectName)
}

export const buildReportDetailsFromApiItem = (item) => {
	if (!item) {
		return null
	}

	const dateKey = item.date || item.exp_date

	return {
		id: [
			item.company_id,
			dateKey,
			item.country_code,
			item.type_of_report || item.name,
		].filter(Boolean).join('-'),
		company: item.company_name,
		jurisdictionCode: item.country_code,
		jurisdictionName: formatReportJurisdiction(item),
		reportType: item.type_of_report || item.name || '',
		dueDateLabel: formatReportDueDateLabel(item.exp_date || item.date),
		frequency: formatReportFrequency(item.period_type),
		period: formatReportPeriod(item),
		progressSteps: mapProgressStepsFromApi(item.progress),
		variant: mapReportItemToVariant(item),
		reportId: getReportCalendarReportId(item),
		hasDeclaration: hasReportCalendarDeclaration(item),
		canViewReturn: canViewReportCalendarReturn(item),
		source: item,
	}
}

export const buildReportDetailsFromTimelineTag = ({ tag }) => (
	buildReportDetailsFromApiItem(tag?.source)
)

export const buildReportDetailsFromUpcomingItem = (item) => (
	buildReportDetailsFromApiItem(item?.source || item)
)
